import { Fragment, useId, useState } from 'react'
import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown, Copy } from 'lucide-react'
import { Button, IconButton } from '@/components/common'
import {
  conversionRate,
  couponLabel,
  formatCount,
  formatDate,
  formatHandle,
  formatSuggestedStake,
  linkEvents,
  linkTitle,
  moneyParts,
  placementLabel,
  stakeParts,
  statusLabel,
} from './affiliateLinksFormat'
import styles from './LiveLinksPage.module.css'

// Numbers are right-aligned, so a sortable header puts its arrow before the
// label: the label's right edge then lines up with the numbers under it.
function SortHeader({ label, name, column, sort, onSort, numeric = true }) {
  const active = sort.by === column
  const icon = (
    <span className={styles.sortIcon} aria-hidden="true">
      {active ? (sort.dir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />) : <ChevronsUpDown size={14} />}
    </span>
  )
  return (
    <th scope="col" className={numeric ? styles.numeric : undefined} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={styles.sortButton} onClick={() => onSort(column)} aria-label={name}>
        {numeric ? icon : null}
        <span>{label}</span>
        {numeric ? null : icon}
      </button>
    </th>
  )
}

// The funnel columns every grouping shares, in the same order and widths.
function FunnelHeaders({ sort, onSort }) {
  return (
    <>
      <SortHeader label="Opens" column="opens" sort={sort} onSort={onSort} />
      <SortHeader label="Added" name="Added to slip" column="adds" sort={sort} onSort={onSort} />
      <SortHeader label="Bets" column="bets" sort={sort} onSort={onSort} />
      <SortHeader label="Conversion" column="conversion" sort={sort} onSort={onSort} />
      <SortHeader label="Staked" column="stake" sort={sort} onSort={onSort} />
      <SortHeader label="GGR" column="ggr" sort={sort} onSort={onSort} />
    </>
  )
}

function FunnelColumns() {
  return (
    <>
      <col className={styles.colCount} />
      <col className={styles.colCount} />
      <col className={styles.colCount} />
      <col className={styles.colRate} />
      <col className={styles.colMoney} />
      <col className={styles.colMoney} />
    </>
  )
}

// The stake in the currency the page shows; a row also staked in another
// currency lists that amount under it, never added in.
function StakeCell({ stake, currency }) {
  const { shown, others } = stakeParts(stake, currency)
  return (
    <td className={styles.numeric}>
      <span className={styles.cellMain}>{shown}</span>
      {others.map((amount) => <span key={amount} className={styles.cellSub}>{amount}</span>)}
    </td>
  )
}

function MoneyCell({ amounts, currency }) {
  const { shown, others } = moneyParts(amounts, currency)
  return (
    <td className={styles.numeric}>
      <span className={styles.cellMain}>{shown}</span>
      {others.map((amount) => <span key={amount} className={styles.cellSub}>{amount}</span>)}
    </td>
  )
}

function FunnelCells({ stats, currency }) {
  return (
    <>
      <td className={styles.numeric}>{formatCount(stats?.opens)}</td>
      <td className={styles.numeric}>{formatCount(stats?.adds)}</td>
      <td className={styles.numeric}>{formatCount(stats?.bets)}</td>
      <td className={styles.numeric}>{conversionRate(stats?.bets || 0, stats?.opens || 0)}</td>
      <StakeCell stake={stats?.stake_by_currency} currency={currency} />
      <MoneyCell amounts={stats?.ggr_by_currency} currency={currency} />
    </>
  )
}

function LinkStatus({ status }) {
  return (
    <span className={styles.status} data-status={status}>
      <span className={styles.statusDot} aria-hidden="true" />
      {statusLabel(status)}
    </span>
  )
}

// Who a link credits: the affiliate for a marketing link, the sharing player
// for a player link. Two lines either way, so rows keep one height.
function LinkOwner({ link }) {
  if (link.source === 'marketing') {
    return (
      <span className={styles.stack}>
        <span className={styles.primary} title={link.affiliate ? formatHandle(link.affiliate.handle) : undefined}>
          {link.affiliate ? formatHandle(link.affiliate.handle) : 'Affiliate not found'}
        </span>
        <span className={styles.secondary}>
          <span className={styles.sourceTag}>Marketing</span>
          {link.affiliate ? <span className={styles.ellipsis} title={link.affiliate.name}>{link.affiliate.name}</span> : null}
        </span>
      </span>
    )
  }
  const playerId = link.sharer?.kambi_player_id
  return (
    <span className={styles.stack}>
      {playerId
        ? <span className={`${styles.primary} ${styles.mono}`} title={playerId}>{playerId}</span>
        : <span className={`${styles.primary} ${styles.muted}`}>Player ID not captured</span>}
      <span className={styles.secondary}>Player link</span>
    </span>
  )
}

function LinkDetail({ link, canEnd, onEnd }) {
  const legs = Array.isArray(link.legs) ? link.legs : []
  const marketing = link.source === 'marketing'
  return (
    <div className={styles.detail}>
      <div className={styles.detailColumn}>
        <h3 className={styles.detailTitle}>Selections</h3>
        {legs.length ? (
          <ul className={styles.legs}>
            {legs.map((leg) => (
              <li key={leg.outcome_id} className={styles.leg}>
                <span className={styles.legText}>
                  <strong>{leg.label}</strong>
                  {leg.event || leg.market ? <span className={styles.muted}>{[leg.event, leg.market].filter(Boolean).join(' · ')}</span> : null}
                </span>
                {leg.odds ? <span className={styles.legOdds}>{leg.odds}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>{link.outcome_count || 0} selections, shared before selection details were recorded.</p>
        )}
        {link.note ? (
          <div className={styles.note}>
            <h3 className={styles.detailTitle}>Internal note</h3>
            <p>{link.note}</p>
          </div>
        ) : null}
      </div>
      <div className={styles.detailSide}>
        <dl className={styles.detailFacts}>
          {marketing ? (
            <div>
              <dt>Affiliate</dt>
              <dd>{link.affiliate ? `${formatHandle(link.affiliate.handle)} · ${link.affiliate.name}` : '—'}</dd>
            </div>
          ) : null}
          <div><dt>Bet type</dt><dd>{couponLabel(link.coupon_type)}</dd></div>
          {link.sport ? <div><dt>Sport</dt><dd>{link.sport}</dd></div> : null}
          {marketing ? <div><dt>Suggested stake</dt><dd>{formatSuggestedStake(link.suggested_stake)}</dd></div> : null}
          <div><dt>{marketing ? 'Created in' : 'Shared from'}</dt><dd>{placementLabel(link.placement)}</dd></div>
          <div><dt>Created</dt><dd>{formatDate(link.created_at)}</dd></div>
          <div><dt>Expires</dt><dd>{formatDate(link.expires_at)}</dd></div>
          <div><dt>People reached</dt><dd>{formatCount(link.stats?.people)}</dd></div>
          {marketing ? null : <div><dt>Self-referred bets</dt><dd>{formatCount(link.stats?.self_referral_bets)}</dd></div>}
        </dl>
        {canEnd && link.status === 'active' ? (
          <div className={styles.detailActions}>
            <Button variant="secondary" size="compact" onClick={() => onEnd(link)} aria-label={`End link ${link.code}`}>End link</Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function LinksTable({ items, sort, onSort, currency, onCopy, canEnd, onEnd }) {
  const [expanded, setExpanded] = useState(() => new Set())
  const baseId = useId()
  const toggle = (id) => setExpanded((current) => {
    const next = new Set(current)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colLink} />
        <col className={styles.colOwner} />
        <FunnelColumns />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Link</th>
          <th scope="col">Credited to</th>
          <FunnelHeaders sort={sort} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {items.map((link) => {
          const open = expanded.has(link.id)
          const detailId = `${baseId}-${link.id}`
          const events = linkEvents(link)
          return (
            <Fragment key={link.id}>
              <tr className={styles.row}>
                <td>
                  <button
                    type="button"
                    className={styles.expandButton}
                    aria-expanded={open}
                    aria-controls={open ? detailId : undefined}
                    onClick={() => toggle(link.id)}
                  >
                    <ChevronRight size={16} aria-hidden="true" className={styles.expandIcon} data-open={open || undefined} />
                    <span className={styles.linkTitle}>{linkTitle(link)}</span>
                  </button>
                  <div className={styles.linkMeta}>
                    <LinkStatus status={link.status} />
                    <span className={styles.code}>{link.code}</span>
                    {link.url ? (
                      <IconButton variant="ghost" label={`Copy link ${link.code}`} className={styles.copyButton} onClick={() => onCopy(link)}>
                        <Copy size={14} aria-hidden="true" />
                      </IconButton>
                    ) : null}
                    {events ? <span className={styles.ellipsis} title={events}>{events}</span> : null}
                  </div>
                </td>
                <td><LinkOwner link={link} /></td>
                <FunnelCells stats={link.stats} currency={currency} />
              </tr>
              {open ? (
                <tr id={detailId} className={styles.detailRow}>
                  <td colSpan={8}><LinkDetail link={link} canEnd={canEnd} onEnd={onEnd} /></td>
                </tr>
              ) : null}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

export function SharersTable({ items, sort, onSort, currency }) {
  return (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colLink} />
        <col className={styles.colDate} />
        <col className={styles.colCount} />
        <FunnelColumns />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Kambi player ID</th>
          <SortHeader label="Last shared" column="last_shared_at" sort={sort} onSort={onSort} numeric={false} />
          <SortHeader label="Links" column="links" sort={sort} onSort={onSort} />
          <FunnelHeaders sort={sort} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {items.map((sharer) => (
          <tr key={sharer.feed_user_id} className={styles.row}>
            <td>
              {sharer.kambi_player_id
                ? <span className={`${styles.primary} ${styles.mono}`}>{sharer.kambi_player_id}</span>
                : <span className={styles.muted}>Not captured</span>}
            </td>
            <td>{formatDate(sharer.last_shared_at)}</td>
            <td className={styles.numeric}>
              <span className={styles.cellMain}>{formatCount(sharer.links)}</span>
              <span className={styles.cellSub}>{formatCount(sharer.active_links)} active</span>
            </td>
            <FunnelCells stats={sharer.stats} currency={currency} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function AffiliatesTable({ items, sort, onSort, currency, onSelect }) {
  return (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.colLink} />
        <col className={styles.colDate} />
        <col className={styles.colCount} />
        <FunnelColumns />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Affiliate</th>
          <th scope="col">Payout reference</th>
          <SortHeader label="Links" column="links" sort={sort} onSort={onSort} />
          <FunnelHeaders sort={sort} onSort={onSort} />
        </tr>
      </thead>
      <tbody>
        {items.map((row) => (
          <tr key={row.affiliate.id} className={styles.row}>
            <td>
              <span className={styles.stack}>
                <button type="button" className={styles.nameButton} onClick={() => onSelect(row.affiliate.id)} aria-label={`Show links for ${formatHandle(row.affiliate.handle)}`}>
                  {row.affiliate.name}
                </button>
                <span className={styles.secondary}>
                  {formatHandle(row.affiliate.handle)}
                  {row.affiliate.active ? null : <span className={styles.inactiveTag}>Inactive</span>}
                </span>
              </span>
            </td>
            <td>{row.affiliate.payout_reference ? <span className={styles.mono}>{row.affiliate.payout_reference}</span> : <span className={styles.muted}>None</span>}</td>
            <td className={styles.numeric}>
              <span className={styles.cellMain}>{formatCount(row.links)}</span>
              <span className={styles.cellSub}>{formatCount(row.active_links)} active</span>
            </td>
            <FunnelCells stats={row.stats} currency={currency} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}
